import React, { createContext, useState, useContext, useEffect } from 'react';
import { loginUser, registerUser, getUserProfile, updateUserProfile } from '../services/api';
import axios from 'axios';

// API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Create context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = async () => {
    try {
      // Call logout API to clear HTTP-only cookies
      await axios.post(`${API_URL}/api/users/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  useEffect(() => {
    const checkLoggedIn = async () => {
      // Check authentication using HTTP-only cookies (no manual token handling needed)

      try {
        // Get user profile with the token
        const response = await getUserProfile();
        if (response.success) {
          setUser(response.user);
        } else {
          setUser(null);
        }
      } catch (error) {
        // Only log in development - don't expose errors in production
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching user profile:', error);
        }
        setUser(null);
      }

      setLoading(false);
    };

    checkLoggedIn();
  }, []);

  // Separate effect for auto-logout listener
  useEffect(() => {
    // Listen for auto-logout events from API interceptor (403/401 errors)
    const handleAutoLogout = async (event) => {
      // event.detail?.reason contains logout reason if needed
      if (user) {
        try {
          await logout();
        } catch (err) {
          // Ignore logout errors
        }
        // Redirect to login if not already there
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
      }
    };
    
    window.addEventListener('auth:logout', handleAutoLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleAutoLogout);
    };
  }, [user]);

  const login = async (credentials, isFromVerification = false) => {
    if (isFromVerification) {
      // If coming from verification, credentials is the user object
      // HTTP-only cookies are already set by the backend
      setUser(credentials);
      return { success: true };
    }

    try {
      const response = await loginUser(credentials);

      if (response.success) {
        // HTTP-only cookies are set automatically by the backend
        setUser(response.user);
      }

      return response;
    } catch (error) {
      // Check if this is an email verification error
      if (error.response?.status === 403 && error.response?.data?.requiresVerification) {
        return {
          success: false,
          error: error.response.data.error,
          requiresVerification: true,
          message: error.response.data.message
        };
      }
      
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', error);
      }
      return { success: false, error: error.message };
    }
  };

  const register = async (userData, referralRef) => {
    try {
      const response = await registerUser(userData, referralRef);
      
      if (response.success) {
        // If verification is required, don't set user or token yet
        if (!response.requiresVerification) {
          // HTTP-only cookies are set automatically by the backend
          setUser(response.user);
        }
      }
      
      return response;
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await axios.post(`${API_URL}/api/users/resend-verification`, { email });
      return response.data;
    } catch (error) {
      console.error('Resend verification error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.message 
      };
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(prev => ({
      ...prev,
      ...updatedUserData
    }));
  };

  const updateProfile = async (userData) => {
    try {
      const response = await updateUserProfile(userData);
      if (response.success) {
        setUser(prev => ({
          ...prev,
          ...response.user
        }));
        return { success: true, user: response.user };
      }
      return { success: false, error: response.error };
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message };
    }
  };

  const setAuthToken = async (token) => {
    // HTTP-only cookies are set by the backend, no need to store manually
    try {
      const response = await getUserProfile();
      if (response.success) {
        setUser(response.user);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const refetchUser = async () => {
    try {
      const response = await getUserProfile();
      if (response.success) {
        setUser(response.user);
      }
    } catch (error) {
      console.error('Error refetching user profile:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: () => !!user,
        login,
        register,
        logout,
        updateUser,
        updateProfile,
        resendVerification,
        refetchUser,
        setAuthToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
